import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { cumulativeDays, previewMessage } from "../followUpConstants";
import styles from "./SequenceCard.module.css";

const PREVIEW_MAX = 60;

function truncate(text, max) {
  const clean = (text ?? "").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).trimEnd()}…`;
}

/**
 * Card de uma cadência. Mostra status (ativa/inativa), badge PADRÃO, resumo,
 * steps com "Dia X" acumulado + preview truncado, e métricas cruzadas.
 *
 * O menu `[···]` expõe: Editar / Definir como padrão (se não for) /
 * Desativar | Reativar / Excluir. As ações são delegadas ao pai.
 */
export default function SequenceCard({
  sequence,
  metrics = null,
  onEdit,
  onSetDefault,
  onDeactivate,
  onReactivate,
  onDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const menuId = useId();

  const steps = Array.isArray(sequence.steps)
    ? sequence.steps.slice().sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
    : [];
  const days = cumulativeDays(steps);
  const totalDays = days.length > 0 ? days[days.length - 1] : 0;
  const isActive = sequence.isActive !== false;

  // Fecha o menu ao clicar fora ou pressionar Esc.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !menuBtnRef.current?.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuBtnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const runAction = (fn) => {
    setMenuOpen(false);
    fn?.();
  };

  const total = metrics?.total ?? 0;
  const converted = metrics?.converted ?? 0;
  const rate =
    metrics?.conversionRate != null
      ? Math.round(metrics.conversionRate)
      : total > 0
        ? Math.round((converted / total) * 100)
        : 0;

  return (
    <article className={`${styles.card} ${isActive ? "" : styles.cardInactive}`.trim()}>
      <div className={styles.top}>
        <div className={styles.badges}>
          <span
            className={`${styles.statusBadge} ${isActive ? styles.statusActive : styles.statusInactive}`}
          >
            <span aria-hidden="true">{isActive ? "🟢" : "⚫"}</span>
            {isActive ? "Ativa" : "Inativa"}
          </span>
          {sequence.isDefault && (
            <span className={styles.defaultBadge}>
              <span aria-hidden="true">⭐</span> PADRÃO
            </span>
          )}
        </div>

        <div className={styles.menuWrap}>
          <button
            type="button"
            ref={menuBtnRef}
            className={styles.menuBtn}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={`Ações da cadência ${sequence.name}`}
          >
            ⋯
          </button>
          {menuOpen && (
            <div ref={menuRef} id={menuId} className={styles.menu} role="menu">
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => runAction(onEdit)}
              >
                Editar
              </button>
              {isActive && !sequence.isDefault && (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => runAction(onSetDefault)}
                >
                  Definir como padrão
                </button>
              )}
              {isActive ? (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => runAction(onDeactivate)}
                >
                  Desativar
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => runAction(onReactivate)}
                >
                  Reativar
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={() => runAction(onDelete)}
              >
                Excluir
              </button>
            </div>
          )}
        </div>
      </div>

      <h3 className={styles.name}>{sequence.name}</h3>
      <p className={styles.summary}>
        {steps.length} {steps.length === 1 ? "mensagem" : "mensagens"} ao longo de{" "}
        {totalDays} {totalDays === 1 ? "dia" : "dias"}
      </p>

      {steps.length > 0 && (
        <ol className={styles.steps}>
          {steps.map((step, i) => (
            <li key={step.id ?? i} className={styles.stepRow}>
              <span className={styles.stepDay}>Dia {days[i]}</span>
              <span className={styles.stepPreview}>
                {truncate(previewMessage(step.messageTemplate), PREVIEW_MAX)}
              </span>
            </li>
          ))}
        </ol>
      )}

      <div className={styles.metricsRow}>
        <span className={styles.metric}>
          <span aria-hidden="true">📊</span> {total}{" "}
          {total === 1 ? "contato" : "contatos"}
        </span>
        <span className={styles.metricDivider} aria-hidden="true">
          ·
        </span>
        <span className={styles.metric}>
          <span aria-hidden="true">🎉</span> {converted} convertidos ({rate}%)
        </span>
      </div>
    </article>
  );
}

SequenceCard.propTypes = {
  sequence: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    isDefault: PropTypes.bool,
    steps: PropTypes.array,
  }).isRequired,
  metrics: PropTypes.shape({
    total: PropTypes.number,
    converted: PropTypes.number,
    conversionRate: PropTypes.number,
  }),
  onEdit: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
